// Admin-side writes: videos, bulk actions, brands/studios, legacy migration.

import {
  addDoc,
  arrayUnion,
  collection,
  deleteField,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  Timestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../utils/firebase";
import { getFavicon } from "../utils/URLInfos";
import { Entity, Video, VideoStatus } from "../types/cms";
import { invalidateCatalog, normalizeEntity } from "./catalog";
import { uploadMedia } from "./media";
import { parseVideoSource } from "./videoSources";
import { domainOf } from "./enrich";

export type VideoInput = Omit<Video, "id" | "createdAt" | "modifiedAt" | "publishedAt">;

function withoutUndefined<T extends object>(data: T): T {
  return Object.fromEntries(Object.entries(data).filter(([, v]) => v !== undefined)) as T;
}

/** Creates (id = null) or updates a video. Returns its id. */
export async function saveVideo(
  id: string | null,
  input: VideoInput,
  previousStatus?: VideoStatus,
): Promise<string> {
  const fields: Record<string, any> = {
    ...input,
    sourceKey: input.videoURL ? parseVideoSource(input.videoURL).sourceKey : input.sourceKey,
    modifiedAt: serverTimestamp(),
  };
  // On update, an emptied optional field (e.g. duration) must be removed,
  // not silently kept; Firestore rejects undefined values.
  const payload: Record<string, any> = id
    ? Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, v === undefined ? deleteField() : v]))
    : withoutUndefined(fields);
  if (input.status === "published" && previousStatus !== "published") {
    payload.publishedAt = serverTimestamp();
  }

  let videoId = id;
  if (id) {
    await updateDoc(doc(db, "videos", id), payload);
  } else {
    const created = await addDoc(collection(db, "videos"), {
      ...payload,
      createdAt: serverTimestamp(),
    });
    videoId = created.id;
  }
  invalidateCatalog();
  return videoId!;
}

async function inBatches(ids: string[], apply: (batch: ReturnType<typeof writeBatch>, id: string) => void) {
  for (let i = 0; i < ids.length; i += 450) {
    const batch = writeBatch(db);
    ids.slice(i, i + 450).forEach((id) => apply(batch, id));
    await batch.commit();
  }
  invalidateCatalog();
}

export type BulkPatch = Partial<Pick<Video, "status" | "agencyId" | "enterpriseId">> & {
  addIndustries?: string[];
  addFormats?: string[];
  addStyles?: string[];
};

export async function bulkUpdateVideos(videos: Pick<Video, "id" | "status">[], patch: BulkPatch) {
  const byId = new Map(videos.map((v) => [v.id, v]));
  await inBatches(Array.from(byId.keys()), (batch, id) => {
    const current = byId.get(id)!;
    const update: Record<string, any> = { modifiedAt: serverTimestamp() };
    if (patch.status) {
      update.status = patch.status;
      if (patch.status === "published" && current.status !== "published") {
        update.publishedAt = serverTimestamp();
      }
    }
    if (patch.agencyId) update.agencyId = patch.agencyId;
    if (patch.enterpriseId) update.enterpriseId = patch.enterpriseId;
    // arrayUnion merges server-side, so edits made elsewhere since the list
    // was loaded are kept.
    if (patch.addIndustries?.length) update.industries = arrayUnion(...patch.addIndustries);
    if (patch.addFormats?.length) update.formats = arrayUnion(...patch.addFormats);
    if (patch.addStyles?.length) update.styles = arrayUnion(...patch.addStyles);
    batch.update(doc(db, "videos", id), update);
  });
}

export async function deleteVideos(ids: string[]) {
  await inBatches(ids, (batch, id) => batch.delete(doc(db, "videos", id)));
}

// ---- Brands & studios --------------------------------------------------------

export type EntityKind = "companies" | "agencies";

export const ENTITY_LABEL: Record<EntityKind, string> = {
  companies: "marque",
  agencies: "studio",
};

/**
 * Creates a brand or studio. Without a logo file, tries the website favicon.
 */
export async function createEntity(
  kind: EntityKind,
  data: { name: string; websiteURL?: string; description?: string; logoFile?: File | null },
): Promise<Entity> {
  let logoURL: string[] = [];
  if (data.logoFile) {
    logoURL = [await uploadMedia("logos", data.logoFile, data.name)];
  } else if (data.websiteURL) {
    // The favicon is a best-effort bonus: never block the creation on it.
    try {
      const favicon = await getFavicon(data.websiteURL);
      if (favicon?.type.startsWith("image/")) logoURL = [await uploadMedia("logos", favicon, data.name)];
    } catch (err) {
      console.warn("Favicon ignoré :", err);
    }
    // Most sites block downloading their icon from the browser (CORS): fall
    // back to Google's public favicon service, displayed directly.
    const host = domainOf(data.websiteURL);
    if (!logoURL.length && host) logoURL = [`https://www.google.com/s2/favicons?domain=${host}&sz=128`];
  }
  const record = {
    name: data.name.trim(),
    websiteURL: (data.websiteURL ?? "").trim(),
    description: (data.description ?? "").trim(),
    logoURL,
    createdAt: Timestamp.now(),
  };
  const created = await addDoc(collection(db, kind), record);
  invalidateCatalog();
  return normalizeEntity(created.id, record);
}

export async function deleteEntity(kind: EntityKind, id: string) {
  await deleteDoc(doc(db, kind, id));
  invalidateCatalog();
}

// ---- Legacy data migration ---------------------------------------------------

export interface MigrationReport {
  videosPublished: number;
  videosKeyed: number;
  entitiesNormalised: number;
}

/**
 * One-off, idempotent migration of documents created before the CMS rework:
 * - videos without `status` become "published" (they were public already),
 * - videos get a `sourceKey` for duplicate detection,
 * - brand/studio `logoURL` strings become arrays.
 */
export async function migrateLegacyData(): Promise<MigrationReport> {
  const report: MigrationReport = { videosPublished: 0, videosKeyed: 0, entitiesNormalised: 0 };
  const videos = await getDocs(collection(db, "videos"));
  const updates: { id: string; data: Record<string, any> }[] = [];

  videos.docs.forEach((d) => {
    const v = d.data() as Video;
    const data: Record<string, any> = {};
    if (!v.status) {
      data.status = "published";
      data.publishedAt = v.createdAt ?? Timestamp.now();
      report.videosPublished++;
    }
    if (!v.sourceKey && v.videoURL) {
      data.sourceKey = parseVideoSource(v.videoURL).sourceKey;
      report.videosKeyed++;
    }
    if (Object.keys(data).length) updates.push({ id: d.id, data });
  });

  const updateMap = new Map(updates.map((u) => [u.id, u.data]));
  await inBatches(Array.from(updateMap.keys()), (batch, id) =>
    batch.update(doc(db, "videos", id), updateMap.get(id)!),
  );

  for (const kind of ["companies", "agencies"] as EntityKind[]) {
    const snap = await getDocs(collection(db, kind));
    const toFix = snap.docs.filter((d) => typeof d.data().logoURL === "string");
    for (const d of toFix) {
      await updateDoc(d.ref, { logoURL: [d.data().logoURL] });
      report.entitiesNormalised++;
    }
  }
  invalidateCatalog();
  return report;
}
